import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Edit3, PlugZap, CheckCircle2, AlertCircle, Lock } from "lucide-react";

const SECRET_LABELS = {
  stripe_api_key: { label: "Stripe Secret Key", hint: "Dashboard Stripe → Developers → API keys" },
  stripe_publishable_key: { label: "Stripe Publishable Key", hint: "Dashboard Stripe → Developers → API keys (começa por pk_)" },
  stripe_webhook_secret: { label: "Stripe Webhook Secret", hint: "Dashboard Stripe → Developers → Webhooks → o endpoint → Signing secret" },
  paypal_client_id: { label: "PayPal Client ID", hint: "PayPal Developer → Apps & Credentials → REST API app" },
  paypal_secret: { label: "PayPal Secret", hint: "Mesma página que o Client ID — botão Show" },
  ifthenpay_mbway_key: { label: "Chave MB WAY", hint: "Backoffice ifthenpay → Chaves API → MB WAY" },
  ifthenpay_mb_key: { label: "Chave Multibanco", hint: "Backoffice ifthenpay → Chaves API → Multibanco" },
  ifthenpay_pix_key: { label: "Chave PIX", hint: "Backoffice ifthenpay → Chaves API → PIX" },
  ifthenpay_antiphishing_key: { label: "Anti-Phishing Key", hint: "Configurada no callback URL do Backoffice ifthenpay" },
  ifthenpay_backoffice_key: { label: "Backoffice Key (reconciliação)", hint: "Chave DIFERENTE das Gateway. Backoffice ifthenpay → Chaves API → Backoffice Key" },
};

function sourceBadge(source) {
  if (source === "db") return <span className="text-[10px] font-semibold text-[#2D6A4F] bg-[#2D6A4F]/10 px-1.5 py-0.5 rounded">DB</span>;
  if (source === "env") return <span className="text-[10px] font-semibold text-[#B26A00] bg-[#B26A00]/10 px-1.5 py-0.5 rounded">ENV</span>;
  return <span className="text-[10px] font-semibold text-[#0B1D3A]/50 bg-[#0B1D3A]/5 px-1.5 py-0.5 rounded">—</span>;
}

function SecretRow({ name, info, onReplaced }) {
  const meta = SECRET_LABELS[name] || { label: name, hint: "" };
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!val.trim()) return setEditing(false);
    setSaving(true);
    try {
      await api.put("/admin/payment-config", { name, value: val.trim() });
      toast.success(meta.label + " atualizada");
      setVal(""); setEditing(false);
      onReplaced();
    } catch (_) { toast.error("Erro ao gravar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wide font-semibold text-[#0B1D3A]/70">{meta.label}</span>
      </div>
      <div className="flex items-center gap-2">
        {editing ? (
          <>
            <input autoFocus type="password" value={val} onChange={(e) => setVal(e.target.value)}
              placeholder="Colar nova chave…" className="flex-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm font-mono"
              data-testid={`pc-input-${name}`} />
            <button onClick={save} disabled={saving} className="px-3 py-2 rounded-lg bg-[#0B1D3A] text-white text-xs font-bold disabled:opacity-50"
              data-testid={`pc-save-${name}`}>Guardar</button>
            <button onClick={() => { setEditing(false); setVal(""); }} className="px-3 py-2 rounded-lg bg-white border border-[#0B1D3A]/15 text-xs font-semibold"
              data-testid={`pc-cancel-${name}`}>Cancelar</button>
          </>
        ) : (
          <>
            <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm font-mono bg-white">
              {info.configured ? <ShieldCheck size={14} className="text-[#2D9C5A]" /> : <AlertCircle size={14} className="text-[#D97706]" />}
              <span className="text-[#0B1D3A]/80">{info.mask || "não configurada"}</span>
              {info.configured && <span className="text-[10px] font-semibold text-[#2D6A4F] bg-[#2D6A4F]/10 px-1.5 py-0.5 rounded ml-1">CONFIGURADA</span>}
            </div>
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-white border border-[#0B1D3A]/15 text-xs font-semibold hover:bg-[#0B1D3A]/5"
              data-testid={`pc-replace-${name}`}>
              <Edit3 size={13} /> Substituir
            </button>
          </>
        )}
      </div>
      {meta.hint && <p className="text-[11px] text-[#0B1D3A]/50 mt-1">{meta.hint}</p>}
    </div>
  );
}

export default function AdminPaymentCredentials() {
  const [cfg, setCfg] = useState(null);
  const [testing, setTesting] = useState("");
  const [results, setResults] = useState({});

  const load = () => api.get("/admin/payment-config").then((r) => setCfg(r.data)).catch(() => toast.error("Erro"));
  useEffect(() => { load(); }, []);

  const setEnabled = async (name, v) => {
    await api.put("/admin/payment-config", { name, value: v });
    load();
  };
  const setMode = async (name, v) => {
    await api.put("/admin/payment-config", { name, value: v });
    load();
  };
  const test = async (provider) => {
    setTesting(provider);
    try {
      const r = await api.post("/admin/payment-config/test", { provider });
      setResults((old) => ({ ...old, ...r.data }));
    } catch (_) { toast.error("Erro no teste"); }
    finally { setTesting(""); }
  };

  if (!cfg) return <div className="p-6 text-[#0B1D3A]/50">A carregar...</div>;

  const Mode = ({ name, options }) => (
    <div className="flex gap-0 rounded-full overflow-hidden border border-[#0B1D3A]/15 w-fit">
      {options.map((o) => (
        <button key={o} onClick={() => setMode(name, o)}
          className={`px-4 py-1.5 text-xs font-bold uppercase ${cfg[name]?.value === o ? "bg-[#0B1D3A] text-white" : "bg-white text-[#0B1D3A]/70"}`}
          data-testid={`pc-${name}-${o}`}>{o}</button>
      ))}
    </div>
  );

  const TestRow = ({ provider }) => (
    <div className="mt-3">
      <button onClick={() => test(provider)} disabled={testing === provider}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B1D3A] text-white text-xs font-bold disabled:opacity-50"
        data-testid={`pc-test-${provider}`}>
        <PlugZap size={13} /> {testing === provider ? "A testar..." : "Testar ligação"}
      </button>
      {results[provider] && (
        <span className={`ml-3 inline-flex items-center gap-1 text-xs font-semibold ${results[provider].ok ? "text-[#2D9C5A]" : "text-[#E63946]"}`}
          data-testid={`pc-test-result-${provider}`}>
          {results[provider].ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
          {results[provider].ok ? "OK" : "Erro"}
          {results[provider].mode && <span className="text-[#0B1D3A]/50 ml-1">· modo {results[provider].mode}</span>}
          {results[provider].error && <span className="text-[#0B1D3A]/50 ml-1 text-[11px]">({results[provider].error})</span>}
        </span>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="payment-credentials-page">
      <div className="flex items-center gap-3">
        <Lock size={20} className="text-[#0B1D3A]" />
        <h1 className="font-outfit text-2xl font-bold text-[#0B1D3A]">Credenciais das plataformas de pagamento</h1>
      </div>
      <p className="text-sm text-[#0B1D3A]/60 -mt-4">
        As chaves nunca aparecem completas: só os <strong>4 últimos caracteres</strong>. Em "Substituir" cola o novo valor — fica guardado no servidor.
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* STRIPE */}
        <section className="rounded-2xl bg-[#F5F3FF] border border-[#7C3AED]/15 p-5" data-testid="pc-stripe">
          <header className="flex items-center justify-between mb-1">
            <h2 className="font-outfit font-bold text-[#0B1D3A]">Stripe</h2>
            <label className="inline-flex items-center gap-1 text-xs font-bold uppercase">
              <input type="checkbox" checked={!!cfg.stripe_enabled?.value} onChange={(e) => setEnabled("stripe_enabled", e.target.checked)} data-testid="pc-stripe-enabled" />
              Ativo
            </label>
          </header>
          <p className="text-xs text-[#0B1D3A]/60 mb-3">Cartões, Apple/Google Pay (via Checkout)</p>
          <div className="mb-3">
            <span className="text-[11px] uppercase tracking-wide font-semibold text-[#0B1D3A]/70">Modo</span>
            <Mode name="stripe_mode" options={["test", "live"]} />
          </div>
          <SecretRow name="stripe_api_key" info={cfg.stripe_api_key} onReplaced={load} />
          <SecretRow name="stripe_publishable_key" info={cfg.stripe_publishable_key} onReplaced={load} />
          <SecretRow name="stripe_webhook_secret" info={cfg.stripe_webhook_secret} onReplaced={load} />
          <TestRow provider="stripe" />
        </section>

        {/* PAYPAL */}
        <section className="rounded-2xl bg-[#EFF6FF] border border-[#1D4ED8]/15 p-5" data-testid="pc-paypal">
          <header className="flex items-center justify-between mb-1">
            <h2 className="font-outfit font-bold text-[#0B1D3A]">PayPal</h2>
            <label className="inline-flex items-center gap-1 text-xs font-bold uppercase">
              <input type="checkbox" checked={!!cfg.paypal_enabled?.value} onChange={(e) => setEnabled("paypal_enabled", e.target.checked)} data-testid="pc-paypal-enabled" />
              Ativo
            </label>
          </header>
          <p className="text-xs text-[#0B1D3A]/60 mb-3">Conta PayPal e cartões via PayPal Smart Buttons</p>
          <div className="mb-3">
            <span className="text-[11px] uppercase tracking-wide font-semibold text-[#0B1D3A]/70">Modo</span>
            <Mode name="paypal_mode" options={["sandbox", "live"]} />
          </div>
          <SecretRow name="paypal_client_id" info={cfg.paypal_client_id} onReplaced={load} />
          <SecretRow name="paypal_secret" info={cfg.paypal_secret} onReplaced={load} />
          <TestRow provider="paypal" />
        </section>

        {/* IFTHENPAY */}
        <section className="rounded-2xl bg-[#FEFCE8] border border-[#CA8A04]/20 p-5" data-testid="pc-ifthenpay">
          <header className="flex items-center justify-between mb-1">
            <h2 className="font-outfit font-bold text-[#0B1D3A]">ifthenpay</h2>
            <label className="inline-flex items-center gap-1 text-xs font-bold uppercase">
              <input type="checkbox" checked={!!cfg.ifthenpay_enabled?.value} onChange={(e) => setEnabled("ifthenpay_enabled", e.target.checked)} data-testid="pc-ifthenpay-enabled" />
              Ativo
            </label>
          </header>
          <p className="text-xs text-[#0B1D3A]/60 mb-3">Multibanco, MB WAY, PIX (PT/BR)</p>
          <SecretRow name="ifthenpay_mbway_key" info={cfg.ifthenpay_mbway_key} onReplaced={load} />
          <SecretRow name="ifthenpay_mb_key" info={cfg.ifthenpay_mb_key} onReplaced={load} />
          <SecretRow name="ifthenpay_pix_key" info={cfg.ifthenpay_pix_key} onReplaced={load} />
          <SecretRow name="ifthenpay_antiphishing_key" info={cfg.ifthenpay_antiphishing_key} onReplaced={load} />
          <SecretRow name="ifthenpay_backoffice_key" info={cfg.ifthenpay_backoffice_key} onReplaced={load} />
          <TestRow provider="ifthenpay" />
        </section>
      </div>

      {/* Fonte das credenciais */}
      <section className="rounded-2xl border border-dashed border-[#0B1D3A]/15 p-5" data-testid="pc-sources">
        <h3 className="text-[11px] uppercase tracking-wider text-[#0B1D3A]/60 font-bold mb-3">Origem das credenciais ativas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 font-mono text-xs">
          {Object.entries(cfg).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-2">
              <span className="text-[#0B1D3A]/80">{k}</span>
              {sourceBadge(v.source || (v.configured ? "env" : "none"))}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#0B1D3A]/50 mt-3">
          <strong>DB</strong> = configurada via admin. <strong>ENV</strong> = ainda vem do <code>.env</code>. Quando substituis aqui, passa a DB.
        </p>
      </section>
    </div>
  );
}
